"use client"

import { useRef, useState } from "react"
import { useDraggable } from "@dnd-kit/core"
import { CSS } from "@dnd-kit/utilities"
import { differenceInDays } from "date-fns"

import { EventItem } from "./event-item"
import { useCalendarDnd } from "./calendar-dnd-context"

/**
 * @typedef {import("./types").CalendarEvent} CalendarEvent
 */

export function DraggableEvent({
  event,
  view,
  showTime,
  onClick,
  height,
  isMultiDay,
  multiDayWidth,
  isFirstDay = true,
  isLastDay = true,
  "aria-hidden": ariaHidden,
  timezone,
  timeFormat,
}) {
  const { activeId } = useCalendarDnd()
  const elementRef = useRef(null)
  const [dragHandlePosition, setDragHandlePosition] = useState(null)

  const eventStart = new Date(event.start)
  const eventEnd = new Date(event.end)
  const computedIsMultiDay =
    typeof isMultiDay === "boolean"
      ? isMultiDay
      : event.allDay || differenceInDays(eventEnd, eventStart) >= 1

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `${event.id}-${view}`,
      data: {
        event,
        view,
        height: height || elementRef.current?.offsetHeight || null,
        isMultiDay: computedIsMultiDay,
        multiDayWidth,
        dragHandlePosition,
        isFirstDay,
        isLastDay,
      },
    })

  const handleMouseDown = (e) => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect()
      setDragHandlePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
    }
  }

  if (isDragging || activeId === `${event.id}-${view}`) {
    return (
      <div ref={setNodeRef} className="opacity-0" style={{ height: height || "auto" }} />
    )
  }

  const style = transform
    ? {
        transform: CSS.Translate.toString(transform),
        height: height || "auto",
        width:
          computedIsMultiDay && multiDayWidth ? `${multiDayWidth}%` : undefined,
      }
    : {
        height: height || "auto",
        width:
          computedIsMultiDay && multiDayWidth ? `${multiDayWidth}%` : undefined,
      }

  const handleTouchStart = (e) => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect()
      const touch = e.touches[0]
      if (touch) {
        setDragHandlePosition({
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        })
      }
    }
  }

  return (
    <div
      ref={(node) => {
        setNodeRef(node)
        elementRef.current = node
      }}
      style={style}
      className="touch-none"
    >
      <EventItem
        event={event}
        view={view}
        showTime={showTime}
        isFirstDay={isFirstDay}
        isLastDay={isLastDay}
        isDragging={isDragging}
        onClick={onClick}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        dndListeners={listeners}
        dndAttributes={attributes}
        aria-hidden={ariaHidden}
        timezone={timezone}
        timeFormat={timeFormat}
      />
    </div>
  )
}
